import React, { useState, useCallback, useEffect, Dispatch, SetStateAction } from "react";
import { Tab, Tabs } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";

import ScrollTabPanel from "../../../../../shared/tabPanelComponent/ScrollTabPanel";
import a11yScrollableProps from "../../../../../../constants/tabPanelProps/ayScrollableProps";

import SpecificEnrollmentGraphicalInsights from "./specificEnrollmentGraphicalInsights/SpecificEnrollmentGraphicalInsights";
import useRouteParam from "../../../../../../hooks/useRouteParam";
import { api } from "../../../../../../utils/api";
import {
	BBI_CORE_ENROLLMENT_SHORT_NAMES,
	BBI_VEB_ENROLLMENT_SHORT_NAMES
} from "../../../../../../constants/BBIEnrollmentPlansShortName";
import { FeatureAlert } from "../../../../../shared";
import {
	SpcificEnrollmentInsights,
	SpecificEnrollmentPlanAgenda
} from "../../../../../../@types/components/enrollmentTabSection.types";
import { MultiBarChartData } from "../../../../../../@types/charts/mutiBarCharts.types";

import "./enrollmentTabSection.css";

const GreenSwitch = withStyles({
	switchBase: {
		color: "white",
		"&$checked": {
			color: "white"
		},
		"&$checked + $track": {
			backgroundColor: "white"
		}
	},
	checked: {},
	track: {
		backgroundColor: "white"
	}
})(Switch);

const tabStyles = makeStyles({
	customTab: {
		minWidth: "180px !important" // Adjust the width as needed
	}
});

const EnrollmentTabSectionPL: React.FC<SpcificEnrollmentInsights> = ({ refresh, setRefresh }) => {
	const [planCheckedStatus, setPlanCheckedStatus] = useState(false);
	const [selectedCorePlan, setSelectedCorePlan] = useState<string>("Health Plan");
	const [selectedVEBPlan, setSelectedVEBPlan] = useState<string>("Cancer");
	const [specificPlanAgenda, setSpecificPlanAgenda] = useState<SpecificEnrollmentPlanAgenda>({
		totalPremium: 0,
		totalEligibleEmployees: 0,
		totalActiveBenefit: 0,
		totalSubmittedEnrollment: 0,
		totalWaivedEnrollment: 0
	});
	const [donutChartData, setDonutChartData] = useState({
		labels: ["Active", "Eligible"],
		datasets: [
			{
				data: [20, 50],
				backgroundColor: ["#a19de0", "#77c2c5"],
				borderColor: ["#FFFFFF", "#FFFFFF"],
				borderWidth: 1
			}
		]
	});
	const [areaChartYearData, setAreaChartYearData] = useState([
		{
			month: "May",
			previous: 5000,
			current: 9800,
			amt: 2290
		},
		{
			month: "Jun",
			previous: 5500,
			current: 5400,
			amt: 2400
		},
		{
			month: "Jul",
			previous: 4780,
			current: 3908,
			amt: 2000
		},
		{
			month: "Aug",
			previous: 5000,
			current: 5500,
			amt: 2400
		},
		{
			month: "Sep",
			previous: 1890,
			current: 4800,
			amt: 2181
		},
		{
			month: "Oct",
			previous: 4000,
			current: 2400,
			amt: 2400
		},
		{
			month: "Nov",
			previous: 2390,
			current: 3800,
			amt: 2500
		},
		{
			month: "Dec",
			previous: 3490,
			current: 4300,
			amt: 2100
		},
		{
			month: "Jan",
			previous: 4000,
			current: 2400,
			amt: 2400
		},
		{
			month: "Feb",
			previous: 2000,
			current: 2000,
			amt: 2400
		},
		{
			month: "Mar",
			previous: 3000,
			current: 1398,
			amt: 2210
		},
		{
			month: "Apr",
			previous: 4000,
			current: 2400,
			amt: 2400
		}
	]);
	const [multiBarChartData, setMultiBarChartData] = useState<MultiBarChartData>({
		currentActive: [],
		currentHire: []
	});
	const [loadingState, setLoadingState] = useState(false);
	const [tabIndex, setTabIndex] = useState(0);
	const [isVEBPlan, setIsVEBPlan] = useState(false);
	const tabStyleClass = tabStyles();
	const queryGroupNumber = useRouteParam("gn");

	const planNamesInitialToFullName = (param: string, constants: any) => {
		if (Object.keys(constants).includes(param)) {
			return constants[param as keyof typeof constants];
		} else if (Object.values(constants).includes(param)) {
			const keys = Object.keys(constants).filter(
				(key: string) => constants[param as keyof typeof constants] === param
			);
			return keys.length > 0 ? keys[0] : param;
		} else {
			return param;
		}
	};

	const handleChange = (event: any, index: any) => {
		const selectedPlanName = event.target.innerText;
		const _selectedPlan = planNamesInitialToFullName(
			selectedPlanName === "GLTD(Class 1)"
				? "GLTD 1"
				: selectedPlanName === "GLTD(Class 2)"
				? "GLTD 2"
				: selectedPlanName,
			BBI_CORE_ENROLLMENT_SHORT_NAMES
		);
		setTabIndex(index);
		setSelectedCorePlan(_selectedPlan as unknown as string);
	};

	const VEBHandleChange = (event: any, index: any) => {
		const selectedPlanName = event.target.innerText;
		const _selectedPlan = planNamesInitialToFullName(
			selectedPlanName === "Term Life to age 121" ? BBI_VEB_ENROLLMENT_SHORT_NAMES.FPP : selectedPlanName,
			BBI_VEB_ENROLLMENT_SHORT_NAMES
		);
		setTabIndex(index);
		setSelectedVEBPlan(_selectedPlan as unknown as string);
	};

	const donutChartOptions = {
		cutout: "40%", // Adjust this value to control the size of the hole in the middle (donut hole)
		plugins: {
			legend: {
				display: true,
				position: "bottom"
			}
		}
	};

	const getInitBenefitSpecificEnrollmentInsights = useCallback(
		async (queryGroupNumber: number, enrollmentType: string, selectedPlan: string) => {
			const apiPayload = {
				group_number: queryGroupNumber,
				enrollment_type: enrollmentType,
				plan_name: selectedPlan
			};
			const specificEnrollmentInsights = await api.benefitSummary.getBenefitSpecificEnrollmentInsights(
				apiPayload
			);

			if (specificEnrollmentInsights) setLoadingState(false);

			let _totalPremiumOfPlan,
				_totalActiveBenefits,
				_totalEligibleEmployeeCount,
				_totalSubmittedBenefits,
				_totalWaivedBenefits,
				_activeBenefitsCount,
				_eligibleMemberCount,
				_previousActiveCurrentWaiveEnrollmentsCount;

			// eslint-disable-next-line prefer-const
			let _storedDonutChartArrayData = [];

			let may_prev = 0,
				jun_prev = 0,
				jul_prev = 0,
				aug_prev = 0,
				sep_prev = 0,
				oct_prev = 0,
				nov_prev = 0,
				dec_prev = 0,
				jan_prev = 0,
				feb_prev = 0,
				mar_prev = 0,
				apr_prev = 0;

			let may_curr = 0,
				jun_curr = 0,
				jul_curr = 0,
				aug_curr = 0,
				sep_curr = 0,
				oct_curr = 0,
				nov_curr = 0,
				dec_curr = 0,
				jan_curr = 0,
				feb_curr = 0,
				mar_curr = 0,
				apr_curr = 0;

			const months_in_a_year: string[] = [
				"January",
				"February",
				"March",
				"April",
				"May",
				"June",
				"July",
				"August",
				"September",
				"October",
				"November",
				"December"
			];

			if (enrollmentType === "CORE") {
				const {
					specificCoreEnrollmentPlanAgenda: {
						sumOfTotalPremiumOfIndividualPlan,
						eligibleMemberCount: totalEligibleMemberCount,
						totalActiveBenefitsCount,
						totalSubmittedEnrollmentsCount,
						totalWaivedEnrollmentsCount
					},
					inidividualCorePlanEligiblePieChartData: {
						activeBenefitsCount,
						eligibleMemberCount,
						previousActiveCurrentWaiveEnrollmentsCount
					},
					individualCoreEnrollmentEligibleAreaChartData: {
						currentYearEnrollmentData,
						previousYearEnrollmentData
					},
					individualCoreBenefitHistogramData: { currentYearActiveBenefitData, currentYearHireData }
				} = specificEnrollmentInsights;

				//card data
				_totalPremiumOfPlan = sumOfTotalPremiumOfIndividualPlan;
				_totalEligibleEmployeeCount = totalEligibleMemberCount;
				_totalActiveBenefits = totalActiveBenefitsCount;
				_totalSubmittedBenefits = totalSubmittedEnrollmentsCount;
				_totalWaivedBenefits = totalWaivedEnrollmentsCount;

				//donutchart
				_activeBenefitsCount = activeBenefitsCount;
				_eligibleMemberCount = eligibleMemberCount;
				_previousActiveCurrentWaiveEnrollmentsCount = previousActiveCurrentWaiveEnrollmentsCount;
				_storedDonutChartArrayData.push(
					_activeBenefitsCount,
					_eligibleMemberCount,
					_previousActiveCurrentWaiveEnrollmentsCount
				);

				//areachart
				previousYearEnrollmentData.forEach((prevData: any, index: number) => {
					const { month, count } = prevData;

					if (month === "May") {
						may_prev = count;
					} else if (month === "June") {
						jun_prev = count;
					} else if (month === "July") {
						jul_prev = count;
					} else if (month === "August") {
						aug_prev = count;
					} else if (month === "September") {
						sep_prev = count;
					} else if (month === "October") {
						oct_prev = count;
					} else if (month === "November") {
						nov_prev = count;
					} else if (month === "December") {
						dec_prev = count;
					} else if (month === "January") {
						jan_prev = count;
					} else if (month === "February") {
						feb_prev = count;
					} else if (month === "March") {
						mar_prev = count;
					} else if (month === "April") {
						apr_prev = count;
					}
				});
				currentYearEnrollmentData.forEach((currData: any, index: number) => {
					const { month, count } = currData;

					if (month === "May") {
						may_curr = count;
					} else if (month === "June") {
						jun_curr = count;
					} else if (month === "July") {
						jul_curr = count;
					} else if (month === "August") {
						aug_curr = count;
					} else if (month === "September") {
						sep_curr = count;
					} else if (month === "October") {
						oct_curr = count;
					} else if (month === "November") {
						nov_curr = count;
					} else if (month === "December") {
						dec_curr = count;
					} else if (month === "January") {
						jan_curr = count;
					} else if (month === "February") {
						feb_curr = count;
					} else if (month === "March") {
						mar_curr = count;
					} else if (month === "April") {
						apr_curr = count;
					}
				});

				// MULTI HISTOGRAM

				const _currentActiveData: number[] = months_in_a_year.map((month_const: string, index: number) => {
					const monthData = currentYearActiveBenefitData.find(
						({ month, count }: { month: string; count: number }) => month === month_const
					);
					return monthData ? monthData.count : 0;
				});

				const _currentHireData: number[] = months_in_a_year.map((month_const: string, index: number) => {
					const monthData = currentYearHireData.find(
						({ month, count }: { month: string; count: number }) => month === month_const
					);
					return monthData ? monthData.count : 0;
				});

				setMultiBarChartData({
					currentActive: _currentActiveData,
					currentHire: _currentHireData
				});

				// SETTING THE VALUES TO THE STATE VARIABLE
				setSpecificPlanAgenda({
					totalPremium: _totalPremiumOfPlan,
					totalEligibleEmployees: _totalEligibleEmployeeCount,
					totalActiveBenefit: _totalActiveBenefits,
					totalSubmittedEnrollment: _totalSubmittedBenefits,
					totalWaivedEnrollment: _totalWaivedBenefits
				});

				setDonutChartData({
					labels: ["Active", "Eligible", "Waived"],
					datasets: [
						{
							data: _storedDonutChartArrayData,
							backgroundColor: ["#a19de0", "#77c2c5", "#6488ea"],
							borderColor: ["#FFFFFF", "#FFFFFF", "#6488ea"],
							borderWidth: 1
						}
					]
				});

				setAreaChartYearData([
					{
						month: "May",
						previous: may_prev,
						current: may_curr,
						amt: 2290
					},
					{
						month: "Jun",
						previous: jun_prev,
						current: jun_curr,
						amt: 2400
					},
					{
						month: "Jul",
						previous: jul_prev,
						current: jul_curr,
						amt: 2000
					},
					{
						month: "Aug",
						previous: aug_prev,
						current: aug_curr,
						amt: 2400
					},
					{
						month: "Sep",
						previous: sep_prev,
						current: sep_curr,
						amt: 2181
					},
					{
						month: "Oct",
						previous: oct_prev,
						current: oct_curr,
						amt: 2400
					},
					{
						month: "Nov",
						previous: nov_prev,
						current: nov_curr,
						amt: 2500
					},
					{
						month: "Dec",
						previous: dec_prev,
						current: dec_curr,
						amt: 2100
					},
					{
						month: "Jan",
						previous: jan_prev,
						current: jan_curr,
						amt: 2400
					},
					{
						month: "Feb",
						previous: feb_prev,
						current: feb_curr,
						amt: 2400
					},
					{
						month: "Mar",
						previous: mar_prev,
						current: mar_curr,
						amt: 2210
					},
					{
						month: "Apr",
						previous: apr_prev,
						current: apr_curr,
						amt: 2400
					}
				]);
			}
			if (enrollmentType === "VEB") {
				const {
					specificVEBEnrollmentPlanAgenda: {
						eligibleMemberCount: totalEligibleMemberCount,
						sumOfTotalPremiumOfIndividualPlan,
						totalActiveBenefitsCount,
						totalSubmittedEnrollmentsCount,
						totalWaivedEnrollmentsCount
					},
					inidividualVEBPlanEligiblePieChartData: {
						activeBenefitsCount,
						eligibleMemberCount,
						previousActiveCurrentWaiveEnrollmentsCount
					},
					inidividualVEBPlanEligibleAreaChartData: { currentYearEnrollmentData, previousYearEnrollmentData },
					inidividualVEBPlanEligibleHistogramData: { currentYearActiveBenefitData, currentYearHireData }
				} = specificEnrollmentInsights;

				// card data
				_totalPremiumOfPlan = sumOfTotalPremiumOfIndividualPlan;
				_totalEligibleEmployeeCount = totalEligibleMemberCount;
				_totalActiveBenefits = totalActiveBenefitsCount;
				_totalSubmittedBenefits = totalSubmittedEnrollmentsCount;
				_totalWaivedBenefits = totalWaivedEnrollmentsCount;

				// donutchart
				_activeBenefitsCount = activeBenefitsCount;
				_eligibleMemberCount = eligibleMemberCount;
				_previousActiveCurrentWaiveEnrollmentsCount = previousActiveCurrentWaiveEnrollmentsCount;

				// donut-chart-VEB
				_storedDonutChartArrayData.push(
					_activeBenefitsCount,
					_eligibleMemberCount,
					_previousActiveCurrentWaiveEnrollmentsCount
				);

				// //areachart
				previousYearEnrollmentData.forEach((prevData: any, index: number) => {
					const { month, count } = prevData;

					if (month === "May") {
						may_prev = count;
					} else if (month === "June") {
						jun_prev = count;
					} else if (month === "July") {
						jul_prev = count;
					} else if (month === "August") {
						aug_prev = count;
					} else if (month === "September") {
						sep_prev = count;
					} else if (month === "October") {
						oct_prev = count;
					} else if (month === "November") {
						nov_prev = count;
					} else if (month === "December") {
						dec_prev = count;
					} else if (month === "January") {
						jan_prev = count;
					} else if (month === "February") {
						feb_prev = count;
					} else if (month === "March") {
						mar_prev = count;
					} else if (month === "April") {
						apr_prev = count;
					}
				});
				currentYearEnrollmentData.forEach((currData: any, index: number) => {
					const { month, count } = currData;

					if (month === "May") {
						may_curr = count;
					} else if (month === "June") {
						jun_curr = count;
					} else if (month === "July") {
						jul_curr = count;
					} else if (month === "August") {
						aug_curr = count;
					} else if (month === "September") {
						sep_curr = count;
					} else if (month === "October") {
						oct_curr = count;
					} else if (month === "November") {
						nov_curr = count;
					} else if (month === "December") {
						dec_curr = count;
					} else if (month === "January") {
						jan_curr = count;
					} else if (month === "February") {
						feb_curr = count;
					} else if (month === "March") {
						mar_curr = count;
					} else if (month === "April") {
						apr_curr = count;
					}
				});

				// MULTI HISTOGRAM
				const _currentActiveData: number[] = months_in_a_year.map((month_const: string, index: number) => {
					const monthData = currentYearActiveBenefitData.find(
						({ month, count }: { month: string; count: number }) => month === month_const
					);
					return monthData ? monthData.count : 0;
				});

				const _currentHireData: number[] = months_in_a_year.map((month_const: string, index: number) => {
					const monthData = currentYearHireData.find(
						({ month, count }: { month: string; count: number }) => month === month_const
					);
					return monthData ? monthData.count : 0;
				});

				setMultiBarChartData({
					currentActive: _currentActiveData,
					currentHire: _currentHireData
				});

				// SETTING THE VALUES TO THE STATE VARIABLE
				setSpecificPlanAgenda({
					totalPremium: _totalPremiumOfPlan,
					totalEligibleEmployees: _totalEligibleEmployeeCount,
					totalActiveBenefit: _totalActiveBenefits,
					totalSubmittedEnrollment: _totalSubmittedBenefits,
					totalWaivedEnrollment: _totalWaivedBenefits
				});

				setDonutChartData({
					labels: ["Active", "Eligible", "Waived"],
					datasets: [
						{
							data: _storedDonutChartArrayData,
							backgroundColor: ["#a19de0", "#77c2c5", "#6488ea"],
							borderColor: ["#FFFFFF", "#FFFFFF", "#6488ea"],
							borderWidth: 1
						}
					]
				});

				setAreaChartYearData([
					{
						month: "May",
						previous: may_prev,
						current: may_curr,
						amt: 2290
					},
					{
						month: "Jun",
						previous: jun_prev,
						current: jun_curr,
						amt: 2400
					},
					{
						month: "Jul",
						previous: jul_prev,
						current: jul_curr,
						amt: 2000
					},
					{
						month: "Aug",
						previous: aug_prev,
						current: aug_curr,
						amt: 2400
					},
					{
						month: "Sep",
						previous: sep_prev,
						current: sep_curr,
						amt: 2181
					},
					{
						month: "Oct",
						previous: oct_prev,
						current: oct_curr,
						amt: 2400
					},
					{
						month: "Nov",
						previous: nov_prev,
						current: nov_curr,
						amt: 2500
					},
					{
						month: "Dec",
						previous: dec_prev,
						current: dec_curr,
						amt: 2100
					},
					{
						month: "Jan",
						previous: jan_prev,
						current: jan_curr,
						amt: 2400
					},
					{
						month: "Feb",
						previous: feb_prev,
						current: feb_curr,
						amt: 2400
					},
					{
						month: "Mar",
						previous: mar_prev,
						current: mar_curr,
						amt: 2210
					},
					{
						month: "Apr",
						previous: apr_prev,
						current: apr_curr,
						amt: 2400
					}
				]);
			}
		},
		[]
	);

	const handlePlanSwitchClick = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const { checked } = event.target;
			setPlanCheckedStatus(checked);
			setTabIndex(0);
			setLoadingState(true);

			if (checked === true) {
				setSelectedVEBPlan("Cancer");
				await getInitBenefitSpecificEnrollmentInsights(queryGroupNumber as number, "VEB", "Cancer");
			}

			if (checked === false) {
				setSelectedCorePlan("Health Plan");
				await getInitBenefitSpecificEnrollmentInsights(queryGroupNumber as number, "CORE", "Health Plan");
			}
		},
		[getInitBenefitSpecificEnrollmentInsights, queryGroupNumber]
	);

	useEffect(() => {
		const enrollmentType = planCheckedStatus ? "VEB" : "CORE";
		setLoadingState(true);
		// REFRESH BUTTON CLICKED
		if (refresh) {
			queryGroupNumber && enrollmentType === "CORE"
				? getInitBenefitSpecificEnrollmentInsights(queryGroupNumber as number, enrollmentType, selectedCorePlan)
				: enrollmentType === "VEB"
				? getInitBenefitSpecificEnrollmentInsights(queryGroupNumber as number, enrollmentType, selectedVEBPlan)
				: null;
			setRefresh(false);
		}
		queryGroupNumber && enrollmentType === "CORE"
			? getInitBenefitSpecificEnrollmentInsights(queryGroupNumber as number, enrollmentType, selectedCorePlan)
			: enrollmentType === "VEB"
			? getInitBenefitSpecificEnrollmentInsights(queryGroupNumber as number, enrollmentType, selectedVEBPlan)
			: null;
	}, [
		getInitBenefitSpecificEnrollmentInsights,
		planCheckedStatus,
		queryGroupNumber,
		refresh,
		selectedCorePlan,
		selectedVEBPlan,
		setRefresh,
		tabIndex
	]);

	return (
		<div className="enrollment-tab-section" id="enrollment-tab-section">
			<div className="enrollment-tab-section-container">
				{/* <div className="tab-feature-alert">
					<FeatureAlert />
				</div> */}
				<div className="tab-section-container">
					<div className="specific-enrollment-container" id="specific-enrollment-container">
						<div className="specific-enrollment-header-text">
							{planCheckedStatus ? "Specific VEB Enrollment Details" : "Specific Core Enrollment Details"}
						</div>
						<div className="switch-container" id="switch-container">
							<Grid className="switch-grid-container" container spacing={1}>
								<Grid className="core-plan-switch-container" item>
									Core Plans
								</Grid>

								{isVEBPlan && (
									<>
										<Grid item>
											<GreenSwitch
												checked={planCheckedStatus}
												onChange={handlePlanSwitchClick}
												className="plan-switch-custom"
											/>
										</Grid>
										<Grid className="veb-plan-switch-container" item>
											VEB Plans
										</Grid>
									</>
								)}
							</Grid>
						</div>
					</div>
					{!planCheckedStatus ? (
						<div className="tab-section-main-container" id="tab-section-main-container">
							<div className="tab-container">
								<Tabs
									// orientation="vertical"
									variant="scrollable"
									value={tabIndex}
									onChange={handleChange}
									scrollButtons="auto"
									style={{
										minWidth: "10rem",
										boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 0px 2.6px",
										backgroundColor: "#CBE4DE",
										color: "#2E4F4F"
									}}
									aria-label="Vertical tabs example"
									className="glb-tab-panel-custom"
									TabIndicatorProps={{
										style: {
											border: "none",
											backgroundColor: "#468c8f"
										}
									}}
								>
									<Tab
										label="Health Plan"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(0)}
									/>
									{/* <Tab
										label="Dental Plan"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(1)}
									/>
									<Tab
										label="Vision Plan"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(2)}
									/> */}
									{/* <Tab
										label="GLTD(Class 1)"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(3)}
									/>
									<Tab
										label="GLTD(Class 2)"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(4)}
									/>
									<Tab
										label="KSOP"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(5)}
									/>
									<Tab
										label="Helping Our Own"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(6)}
									/>
									<Tab
										label="GLI"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(7)}
									/>
									<Tab
										label="CCE"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(8)}
									/> */}
								</Tabs>
							</div>
							<div className="graphical-container">
								<ScrollTabPanel value={tabIndex} index={0}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={1}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={2}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={3}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={4}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={5}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={6}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={7}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={8}>
									<div className="tab-section-information-container">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
							</div>
						</div>
					) : (
						<div className="tab-section-main-container" id="tab-section-main-container">
							<div className="tab-container">
								<Tabs
									// orientation="vertical"
									variant="scrollable"
									value={tabIndex}
									onChange={VEBHandleChange}
									style={{
										// minWidth: "10rem",
										boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 0px 2.6px",
										// borderRadius: "0 0 10px 10px",
										backgroundColor: "#CBE4DE",
										color: "#2E4F4F"
									}}
									aria-label="Vertical tabs example"
									className="glb-tab-panel-custom"
									TabIndicatorProps={{
										style: {
											border: "none",
											backgroundColor: "#468c8f"
										}
									}}
								>
									<Tab
										label="Cancer"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(0)}
									/>
									<Tab
										label="Whole Life"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(1)}
									/>
									<Tab
										label="STD"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(2)}
									/>
									<Tab
										label="Critical Illness"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(3)}
									/>
									<Tab
										label="Hospital Indemnity"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(4)}
									/>
									<Tab
										label="Accident"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(5)}
									/>
									<Tab
										label="GLI"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(6)}
									/>
									<Tab
										label="Term Life to age 121"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(7)}
									/>
									<Tab
										label="Doc & Rx"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(8)}
									/>
									<Tab
										label="Vision"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(9)}
									/>
									<Tab
										label="Dental"
										className={`glb-tab-label ${tabStyleClass.customTab}`}
										{...a11yScrollableProps(10)}
									/>
								</Tabs>
							</div>
							<div className="graphical-container">
								<ScrollTabPanel value={tabIndex} index={0}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={1}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={2}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={3}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={4}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={5}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={6}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={7}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={8}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={9}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
								<ScrollTabPanel value={tabIndex} index={10}>
									<div className="tab-section-information-container veb">
										<SpecificEnrollmentGraphicalInsights
											loading={loadingState}
											cardDataProps={specificPlanAgenda}
											donutChartProps={{
												data: donutChartData,
												options: donutChartOptions
											}}
											areaChartProps={{ areaData: areaChartYearData }}
											multiBarChartData={multiBarChartData}
										/>
									</div>
								</ScrollTabPanel>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default EnrollmentTabSectionPL;
